import { createApi } from '@reduxjs/toolkit/query/react';
import { dynamicBaseQuery } from '../badRequestHandler';

/* ─── Types ──────────────────────────────────────────────────────────── */

interface GetSuperAdminKpiParams {
  branchId?:  string;
  startDate?: string;
  endDate?:   string;
}

export interface SuperAdminKpi {
  totalRevenue:  number;
  checkInsToday: number;
  todaySale:     number;
  activeMembers: number;
  totalUsers:    number;
  newJoinees:    number;
}

interface GetSuperAdminKpiResponse {
  success: boolean;
  data:    SuperAdminKpi;
}

interface GetRevenueBreakdownParams {
  branchId?:  string;
  startDate?: string;
  endDate?:   string;
}

export interface RevenueBreakdownItem {
  category: string;
  total:    number;
}

export interface RevenueBreakdownData {
  grandTotal: number;
  breakdown:  RevenueBreakdownItem[];
}

interface GetRevenueBreakdownResponse {
  success: boolean;
  data:    RevenueBreakdownData;
}

interface GetRevenueTrendParams {
  branchId?: string;
  period:    'weekly' | 'monthly' | 'yearly';
  year?:     number;
}

export interface RevenueTrendPoint {
  label: string;
  [category: string]: string | number;
}

export interface RevenueTrendData {
  period:     string;
  categories: string[];
  points:     RevenueTrendPoint[];
}

interface GetRevenueTrendResponse {
  success: boolean;
  data:    RevenueTrendData;
}

interface GetClubLeaderboardParams {
  startDate?: string;
  endDate?:   string;
  limit?:     number;
}

interface GetClubLeaderboardResponse {
  success: boolean;
  data:    unknown;
}

interface GetSalesLeaderboardParams {
  startDate?:    string;
  endDate?:      string;
  limit?:        number;
  salesmanType?: string;
}

interface GetSalesLeaderboardResponse {
  success: boolean;
  data:    unknown;
}

interface GetCrmRegistrationsParams {
  branchId?:  string;
  startDate?: string;
  endDate?:   string;
}

interface GetCrmRegistrationsResponse {
  success: boolean;
  data:    unknown;
}

interface GetPtUtilizationParams {
  branchId?: string;
  limit?:    number;
}

interface GetPtUtilizationResponse {
  success: boolean;
  data:    unknown;
}

/* ─── API ────────────────────────────────────────────────────────────── */

export const dashboardApi = createApi({
  reducerPath: 'dashboardApi',
  baseQuery:   dynamicBaseQuery,
  tagTypes:    ['Dashboard'] as const,
  endpoints:   (builder) => ({
    getSuperAdminKpi: builder.query<GetSuperAdminKpiResponse, GetSuperAdminKpiParams>({
      query: ({ branchId, startDate, endDate } = {}) => {
        const params = new URLSearchParams();
        if (branchId)  params.append('branchId', branchId);
        if (startDate) params.append('startDate', startDate);
        if (endDate)   params.append('endDate', endDate);
        return { url: `/dashboard/super-admin/kpi?${params.toString()}`, method: 'GET' };
      },
      providesTags: ['Dashboard'],
    }),
    getRevenueBreakdown: builder.query<GetRevenueBreakdownResponse, GetRevenueBreakdownParams>({
      query: ({ branchId, startDate, endDate } = {}) => {
        const params = new URLSearchParams();
        if (branchId)  params.append('branchId', branchId);
        if (startDate) params.append('startDate', startDate);
        if (endDate)   params.append('endDate', endDate);
        return { url: `/dashboard/super-admin/revenue-breakdown?${params.toString()}`, method: 'GET' };
      },
      providesTags: ['Dashboard'],
    }),
    getRevenueTrend: builder.query<GetRevenueTrendResponse, GetRevenueTrendParams>({
      query: ({ branchId, period, year } = {} as GetRevenueTrendParams) => {
        const params = new URLSearchParams();
        if (branchId) params.append('branchId', branchId);
        params.append('period', period);
        if (year) params.append('year', String(year));
        return { url: `/dashboard/super-admin/revenue-trend?${params.toString()}`, method: 'GET' };
      },
      providesTags: ['Dashboard'],
    }),
    getClubLeaderboard: builder.query<GetClubLeaderboardResponse, GetClubLeaderboardParams>({
      query: ({ startDate, endDate, limit } = {}) => {
        const params = new URLSearchParams();
        if (startDate) params.append('startDate', startDate);
        if (endDate)   params.append('endDate', endDate);
        if (limit)     params.append('limit', String(limit));
        return { url: `/dashboard/super-admin/club-leaderboard?${params.toString()}`, method: 'GET' };
      },
      providesTags: ['Dashboard'],
    }),
    getSalesLeaderboard: builder.query<GetSalesLeaderboardResponse, GetSalesLeaderboardParams>({
      query: ({ startDate, endDate, limit, salesmanType } = {}) => {
        const params = new URLSearchParams();
        if (startDate)    params.append('startDate', startDate);
        if (endDate)      params.append('endDate', endDate);
        if (limit)        params.append('limit', String(limit));
        if (salesmanType) params.append('salesmanType', salesmanType);
        return { url: `/dashboard/super-admin/sales-leaderboard?${params.toString()}`, method: 'GET' };
      },
      providesTags: ['Dashboard'],
    }),
    getCrmRegistrations: builder.query<GetCrmRegistrationsResponse, GetCrmRegistrationsParams | void>({
      query: (params) => {
        const { branchId, startDate, endDate } = params || {};
        const qp = new URLSearchParams();
        if (branchId)  qp.append('branchId', branchId);
        if (startDate) qp.append('startDate', startDate);
        if (endDate)   qp.append('endDate', endDate);
        return { url: `/dashboard/super-admin/crm-registrations?${qp.toString()}`, method: 'GET' };
      },
      providesTags: ['Dashboard'],
    }),
    getPtUtilization: builder.query<GetPtUtilizationResponse, GetPtUtilizationParams | void>({
      query: (params) => {
        const { branchId, limit } = params || {};
        const qp = new URLSearchParams();
        if (branchId) qp.append('branchId', branchId);
        if (limit)    qp.append('limit', String(limit));
        return { url: `/dashboard/super-admin/pt-utilization?${qp.toString()}`, method: 'GET' };
      },
      providesTags: ['Dashboard'],
    }),
  }),
});

export const {
  useGetSuperAdminKpiQuery,
  useGetRevenueBreakdownQuery,
  useGetRevenueTrendQuery,
  useGetClubLeaderboardQuery,
  useGetSalesLeaderboardQuery,
  useGetCrmRegistrationsQuery,
  useGetPtUtilizationQuery,
} = dashboardApi;
